import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Button,
  DashboardCard,
  DashboardGrid,
  Input,
  Label,
  SectionHeader,
} from '@zafroi/ui-kit';
import { getBotConfig, saveBotConfig, ensureLeadIntakeKey } from '@/services/engage-api';
import { useTenant } from '@/services/tenant-context';
import type { BotConfig } from '@/types/engage';
import { AvatarUpload } from './AvatarUpload';

const MODES = ['LIGHT', 'DARK'] as const;
const POSITIONS = ['bottom-right', 'bottom-left'] as const;
const BRANDING_MODES = ['BOTH', 'LABEL', 'IMAGE'] as const;
const AVATAR_SHAPES = ['ROUND', 'ROUNDED', 'SQUARE', 'ORIGINAL'] as const;
const BOT_MODES = ['SUPPORT', 'SALES'] as const;

/** Human labels for the enum selects. */
const LABELS: Record<string, string> = {
  BOTH: 'Logo + name',
  LABEL: 'Name only',
  IMAGE: 'Logo only',
  ROUND: 'Round (circle)',
  ROUNDED: 'Curved',
  SQUARE: 'Square',
  ORIGINAL: 'Original (actual aspect)',
  SUPPORT: 'Support — post-sale customer support',
  SALES: 'Sales — pre-sale assistant for prospects',
};

/** CSS the preview + widget apply per shape. Images use `contain` so a logo
 *  is never cropped. ORIGINAL keeps the natural aspect (height-fit). */
function avatarStyle(shape: string): React.CSSProperties {
  const base: React.CSSProperties = { height: 24, objectFit: 'contain', flex: '0 0 auto' };
  switch (shape) {
    case 'ORIGINAL':
      return { ...base, width: 'auto', maxWidth: 96, borderRadius: 0 };
    case 'SQUARE':
      return { ...base, width: 24, borderRadius: 0 };
    case 'ROUNDED':
      return { ...base, width: 24, borderRadius: 6 };
    case 'ROUND':
    default:
      return { ...base, width: 24, borderRadius: '50%' };
  }
}

/** Light/dark surface colours — mirror the widget's CSS defaults so the
 *  preview matches what visitors see. */
const SURFACES = {
  LIGHT: { panel: '#ffffff', text: '#111111', msgs: '#f7f7f8', bot: '#ffffff', botBorder: '#ececec' },
  DARK: { panel: '#1f2024', text: '#ececf1', msgs: '#16171a', bot: '#2a2c31', botBorder: '#3a3d44' },
};

/**
 * Appearance — the chatbot widget's display + theme editor. Persists to
 * EngageBotConfig (per tenant); the embedded widget reads it on boot via
 * the widget-session response, so saving here re-skins the live widget.
 */
export function AppearanceSection() {
  const qc = useQueryClient();
  const { currentTenant } = useTenant();
  const configKey = ['bot-config', currentTenant.id];

  const query = useQuery({ queryKey: configKey, queryFn: getBotConfig });

  const [form, setForm] = useState<BotConfig | null>(null);
  // Seed the form once the saved config loads.
  useEffect(() => {
    if (query.data && !form) setForm(query.data);
  }, [query.data, form]);

  const save = useMutation({
    mutationFn: (body: BotConfig) => saveBotConfig(body),
    onSuccess: (saved) => {
      setForm(saved);
      qc.setQueryData(configKey, saved);
      toast.success('Appearance saved — the widget will pick it up on next load.');
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : 'Could not save appearance'),
  });

  const set = <K extends keyof BotConfig>(key: K, value: BotConfig[K]) =>
    setForm((f) => (f ? { ...f, [key]: value } : f));

  // Lead intake key — generated server-side (persisted immediately, independent
  // of the Save button). Rotate when an existing key is shown.
  const genKey = useMutation({
    mutationFn: () => ensureLeadIntakeKey(!!form?.leadIntakeKey),
    onSuccess: ({ leadIntakeKey }) => {
      set('leadIntakeKey', leadIntakeKey);
      qc.setQueryData<BotConfig>(configKey, (d) => (d ? { ...d, leadIntakeKey } : d));
      toast.success('Lead intake key ready — share it with the tenant that should receive your leads.');
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : 'Could not generate intake key'),
  });

  const dirty = useMemo(
    () => !!form && !!query.data && JSON.stringify(form) !== JSON.stringify(query.data),
    [form, query.data],
  );

  return (
    <>
      <SectionHeader
        withDivider
        title="Appearance"
        description="Match the chatbot to your portal — colours, header, greeting and position. Saved branding loads automatically in the embedded widget."
      />

      {query.isLoading || !form ? (
        <DashboardCard title="Appearance">
          <div className="h-40 animate-pulse rounded-md bg-muted" />
        </DashboardCard>
      ) : (
        <DashboardGrid columns={2}>
          {/* ── Editor ───────────────────────────────────────────── */}
          <DashboardCard title="Theme & identity" description="What customers see.">
            <div className="space-y-3">
              <Field label="Bot purpose">
                <Select value={form.botMode} options={BOT_MODES} onChange={(v) => set('botMode', v)} />
              </Field>
              {form.botMode === 'SALES' && (
                <Field label="Assistant persona / instructions">
                  <textarea
                    value={form.systemInstructions ?? ''}
                    onChange={(e) => set('systemInstructions', e.target.value || null)}
                    rows={5}
                    placeholder="e.g. You are ZAFROI's pre-sales assistant. Help prospects with pricing, deployment, and templates; answer only from approved knowledge; offer a demo when they want to talk to a person."
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </Field>
              )}
              <Field label="Bot name">
                <Input value={form.botName} onChange={(e) => set('botName', e.target.value)} />
              </Field>
              <Field label="Header title (optional — defaults to bot name)">
                <Input
                  value={form.headerTitle ?? ''}
                  placeholder={form.botName}
                  onChange={(e) => set('headerTitle', e.target.value || null)}
                />
              </Field>
              <Field label="Opening greeting">
                <Input value={form.greeting} onChange={(e) => set('greeting', e.target.value)} />
              </Field>

              <div className="grid grid-cols-3 gap-3">
                <ColorField label="Primary" value={form.primaryColor} onChange={(v) => set('primaryColor', v)} />
                <ColorField
                  label="Header"
                  value={form.headerColor ?? form.primaryColor}
                  onChange={(v) => set('headerColor', v)}
                />
                <ColorField
                  label="Header text"
                  value={form.headerTextColor}
                  onChange={(v) => set('headerTextColor', v)}
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <Field label="Mode">
                  <Select value={form.mode} options={MODES} onChange={(v) => set('mode', v)} />
                </Field>
                <Field label="Position">
                  <Select value={form.position} options={POSITIONS} onChange={(v) => set('position', v)} />
                </Field>
                <Field label={`Corner radius (${form.cornerRadius}px)`}>
                  <input
                    type="range"
                    min={0}
                    max={32}
                    value={form.cornerRadius}
                    onChange={(e) => set('cornerRadius', Number(e.target.value))}
                    className="h-9 w-full"
                  />
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Header shows">
                  <Select
                    value={form.brandingMode}
                    options={BRANDING_MODES}
                    onChange={(v) => set('brandingMode', v)}
                  />
                </Field>
                <Field label="Logo shape">
                  <Select
                    value={form.avatarShape}
                    options={AVATAR_SHAPES}
                    onChange={(v) => set('avatarShape', v)}
                    disabled={form.brandingMode === 'LABEL'}
                  />
                </Field>
              </div>

              {form.brandingMode !== 'LABEL' && (
                <AvatarUpload
                  value={form.avatarUrl}
                  onChange={(url) => set('avatarUrl', url)}
                  ownerId={currentTenant.id}
                />
              )}

              {/* ── Lead capture (generic; opt-in) ──────────────────── */}
              <div className="space-y-3 rounded-md border border-input p-3">
                <div className="text-sm font-medium">Lead capture</div>
                <p className="text-xs text-muted-foreground">
                  Let the bot offer a call-to-action chip that opens an in-chat form, and
                  choose where captured leads go. Leave the call-to-action empty to turn it off.
                </p>
                <Field label="Call-to-action (e.g. Book a demo, Request a callback)">
                  <Input
                    value={form.leadCtaLabel ?? ''}
                    placeholder="Empty = lead capture off"
                    onChange={(e) => set('leadCtaLabel', e.target.value || null)}
                  />
                </Field>
                <Field label="Notification email (where leads are emailed)">
                  <Input
                    value={form.leadEmail ?? ''}
                    placeholder="e.g. sales@yourcompany.com"
                    onChange={(e) => set('leadEmail', e.target.value || null)}
                  />
                </Field>
                <Field label="External webhook URL (optional — POST leads to your own system)">
                  <Input
                    value={form.leadWebhookUrl ?? ''}
                    placeholder="https://…"
                    onChange={(e) => set('leadWebhookUrl', e.target.value || null)}
                  />
                </Field>
                <Field label="Webhook signing secret (optional)">
                  <Input
                    value={form.leadWebhookSecret ?? ''}
                    onChange={(e) => set('leadWebhookSecret', e.target.value || null)}
                    className="font-mono text-xs"
                  />
                </Field>
                <Field label="Forward to another ZAFROI tenant (paste their intake key)">
                  <Input
                    value={form.forwardIntakeKey ?? ''}
                    placeholder="zlk_…  — leads land in that tenant's inbox"
                    onChange={(e) => set('forwardIntakeKey', e.target.value || null)}
                    className="font-mono text-xs"
                  />
                </Field>
                <Field label="Your intake key (share it so another tenant can forward leads to you)">
                  <div className="flex items-center gap-2">
                    <Input
                      value={form.leadIntakeKey ?? ''}
                      readOnly
                      placeholder="Not generated yet"
                      className="font-mono text-xs"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={genKey.isPending}
                      onClick={() => genKey.mutate()}
                    >
                      {genKey.isPending ? '…' : form.leadIntakeKey ? 'Rotate' : 'Generate'}
                    </Button>
                  </div>
                </Field>
              </div>

              <div className="flex items-center gap-2 pt-1">
                <Button
                  size="sm"
                  disabled={!dirty || save.isPending}
                  onClick={() => form && save.mutate(form)}
                >
                  {save.isPending ? 'Saving…' : 'Save appearance'}
                </Button>
                {dirty && (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={save.isPending}
                    onClick={() => query.data && setForm(query.data)}
                  >
                    Reset
                  </Button>
                )}
              </div>
            </div>
          </DashboardCard>

          {/* ── Live preview ─────────────────────────────────────── */}
          <DashboardCard title="Live preview" description="How the widget looks on your site.">
            <WidgetPreview config={form} />
          </DashboardCard>
        </DashboardGrid>
      )}
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-10 shrink-0 cursor-pointer rounded border border-input bg-background p-0.5"
          aria-label={`${label} colour`}
        />
        <Input value={value} onChange={(e) => onChange(e.target.value)} className="font-mono text-xs" />
      </div>
    </div>
  );
}

function Select<T extends string>({
  value,
  options,
  onChange,
  disabled,
}: {
  value: string;
  options: readonly T[];
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
    >
      {options.map((o) => (
        <option key={o} value={o}>
          {LABELS[o] ?? (o === 'LIGHT' ? 'Light' : o === 'DARK' ? 'Dark' : o)}
        </option>
      ))}
    </select>
  );
}

/** A faithful-enough mock of the embedded widget panel. */
function WidgetPreview({ config }: { config: BotConfig }) {
  const s = SURFACES[config.mode === 'DARK' ? 'DARK' : 'LIGHT'];
  const headerBg = config.headerColor || config.primaryColor;
  const radius = `${config.cornerRadius}px`;
  return (
    <div className="flex justify-center py-2">
      <div
        style={{
          width: 300,
          borderRadius: radius,
          overflow: 'hidden',
          background: s.panel,
          color: s.text,
          boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
          border: `1px solid ${s.botBorder}`,
        }}
      >
        {/* header */}
        <div
          style={{
            background: headerBg,
            color: config.headerTextColor,
            padding: '12px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          {config.brandingMode !== 'LABEL' && config.avatarUrl && (
            <img src={config.avatarUrl} alt="" style={avatarStyle(config.avatarShape)} />
          )}
          {config.brandingMode !== 'IMAGE' && (config.headerTitle || config.botName)}
        </div>
        {/* messages */}
        <div style={{ background: s.msgs, padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Bubble bg={s.bot} color={s.text} border={s.botBorder} align="flex-start">
            {config.greeting}
          </Bubble>
          <Bubble bg={config.primaryColor} color="#fff" align="flex-end">
            What is your return policy?
          </Bubble>
          <Bubble bg={s.bot} color={s.text} border={s.botBorder} align="flex-start">
            Easy 15-day returns. <span style={{ color: config.primaryColor, fontWeight: 600 }}>↗ Returns policy</span>
          </Bubble>
        </div>
        {/* input */}
        <div style={{ display: 'flex', gap: 8, padding: 10, background: s.panel, borderTop: `1px solid ${s.botBorder}` }}>
          <div style={{ flex: 1, border: `1px solid ${s.botBorder}`, borderRadius: 8, padding: '7px 10px', fontSize: 12, color: '#9aa' }}>
            Type your message…
          </div>
          <div style={{ width: 34, height: 34, borderRadius: 8, background: config.primaryColor, color: '#fff', display: 'grid', placeItems: 'center', fontSize: 13 }}>
            ➤
          </div>
        </div>
      </div>
    </div>
  );
}

function Bubble({
  children,
  bg,
  color,
  border,
  align,
}: {
  children: React.ReactNode;
  bg: string;
  color: string;
  border?: string;
  align: 'flex-start' | 'flex-end';
}) {
  return (
    <div
      style={{
        alignSelf: align,
        maxWidth: '82%',
        background: bg,
        color,
        border: border ? `1px solid ${border}` : undefined,
        borderRadius: 13,
        padding: '8px 11px',
        fontSize: 12.5,
        lineHeight: 1.4,
      }}
    >
      {children}
    </div>
  );
}
